import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/common/Button';

const LoginPage = () => {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Super Admin phone number
  const SUPER_ADMIN_PHONE = '9845557639';

  // Default roles (Admin is hidden)
  const [availableRoles, setAvailableRoles] = useState(['Manager', 'Staff']);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Listen to phone number changes and dynamically show/hide Admin role
  useEffect(() => {
    if (phone === SUPER_ADMIN_PHONE) {
      // Add Admin role when super admin phone is entered
      setAvailableRoles(prevRoles => {
        if (!prevRoles.includes('Admin')) {
          return ['Manager', 'Staff', 'Admin'];
        }
        return prevRoles;
      });
      // Auto-select Admin role
      setSelectedRole(prevRole => {
        if (prevRole !== 'Admin') {
          return 'Admin';
        }
        return prevRole;
      });
    } else {
      // Remove Admin role when phone changes away from super admin
      setAvailableRoles(prevRoles => {
        if (prevRoles.includes('Admin')) {
          return ['Manager', 'Staff'];
        }
        return prevRoles;
      });
      // Reset selectedRole if it was Admin
      setSelectedRole(prevRole => {
        if (prevRole === 'Admin') {
          return '';
        }
        return prevRole;
      });
    }
  }, [phone]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate role selection
    if (!selectedRole) {
      setError('Please select a role');
      return;
    }

    setLoading(true);

    const result = await login(phone, password, selectedRole);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error || 'Invalid credentials');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh-screen flex items-center justify-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))] bg-gradient-to-tr from-indigo-100 via-blue-50 to-purple-100 relative">


      <div className="w-full max-w-5xl flex flex-col md:flex-row rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl bg-white dark:bg-slate-800 float-animation border border-slate-100 dark:border-slate-700">
        {/* Branding Section */}
        <div className="hidden md:flex flex-1 bg-gradient-to-br from-primary via-primary-dark to-indigo-700 p-12 flex-col justify-center items-center text-center relative overflow-hidden">
          {/* Animated background elements */}
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10">
            <div className="text-7xl font-extrabold mb-6 text-white relative">
              BizTracker
              <div className="absolute bottom-2 left-1/4 w-1/2 h-1 bg-white/30 rounded-full"></div>
            </div>
            <p className="text-xl text-white/90 mb-12 max-w-sm font-medium">Elevate your business with powerful insights and analytics</p>

            <div className="grid grid-cols-2 gap-4 max-w-md">
              {[
                { icon: 'fas fa-chart-line', label: 'Analytics', color: 'from-blue-400 to-cyan-400' },
                { icon: 'fas fa-lock', label: 'Security', color: 'from-purple-400 to-pink-400' },
                { icon: 'fas fa-sync', label: 'Real-time', color: 'from-green-400 to-emerald-400' },
                { icon: 'fas fa-mobile-alt', label: 'Mobile', color: 'from-orange-400 to-red-400' }
              ].map((feature, idx) => (
                <div key={idx} className="bg-white/10 backdrop-blur-md p-6 rounded-2xl hover:bg-white/20 transition-all duration-300 cursor-pointer border border-white/20 card-hover group">
                  <div className={`bg-gradient-to-br ${feature.color} w-16 h-16 rounded-xl flex items-center justify-center mb-3 mx-auto group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="font-semibold text-sm text-white">
                    {feature.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Login Form Section */}
        <div className="flex-1 p-6 sm:p-8 md:p-12 lg:p-16 flex flex-col justify-center bg-white dark:bg-slate-800">
          <div className="text-center mb-6 sm:mb-8 md:mb-10">
            <div className="inline-block mb-3 sm:mb-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-primary to-accent rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
                <i className="fas fa-user-shield text-xl sm:text-2xl text-white"></i>
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3 gradient-text">
              Welcome Back
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-slate-600 dark:text-slate-400">Sign in to access your professional dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="relative group">
              <label htmlFor="login-phone" className="sr-only">Phone Number</label>
              <i className="fas fa-phone absolute left-4 sm:left-5 top-1/2 transform -translate-y-1/2 text-primary/80 dark:text-accent/80 text-base sm:text-lg z-10 animate-pulse-slow" aria-hidden="true"></i>
              <input
                id="login-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone Number"
                required
                maxLength="10"
                inputMode="numeric"
                autoComplete="tel"
                className="w-full pl-12 sm:pl-14 pr-4 sm:pr-5 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/15 dark:focus:ring-accent/20 focus:border-primary dark:focus:border-accent hover:border-slate-300 dark:hover:border-slate-500 transition-all duration-200 text-sm sm:text-base text-slate-800 dark:text-slate-100 shadow-sm"
              />
            </div>

            <div className="relative group">
              <label htmlFor="login-role" className="sr-only">Select Role</label>
              <i className="fas fa-user-tag absolute left-4 sm:left-5 top-1/2 transform -translate-y-1/2 text-primary/80 dark:text-accent/80 text-base sm:text-lg z-10" aria-hidden="true"></i>
              <select
                id="login-role"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                required
                className="w-full pl-12 sm:pl-14 pr-8 sm:pr-10 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/15 dark:focus:ring-accent/20 focus:border-primary dark:focus:border-accent hover:border-slate-300 dark:hover:border-slate-500 transition-all duration-200 appearance-none cursor-pointer text-sm sm:text-base text-slate-800 dark:text-slate-100 shadow-sm"
              >
                <option value="">Select Role *</option>
                {availableRoles.map((role) => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
              <i className="fas fa-chevron-down absolute right-4 sm:right-5 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none text-xs" aria-hidden="true"></i>
            </div>

            <div className="relative group">
              <label htmlFor="login-password" className="sr-only">Password</label>
              <i className="fas fa-lock absolute left-4 sm:left-5 top-1/2 transform -translate-y-1/2 text-primary/80 dark:text-accent/80 text-base sm:text-lg z-10" aria-hidden="true"></i>
              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                autoComplete="current-password"
                className="w-full pl-12 sm:pl-14 pr-4 sm:pr-5 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/15 dark:focus:ring-accent/20 focus:border-primary dark:focus:border-accent hover:border-slate-300 dark:hover:border-slate-500 transition-all duration-200 text-sm sm:text-base text-slate-800 dark:text-slate-100 shadow-sm"
              />
            </div>

            <Button
              type="submit"
              loading={loading}
              fullWidth
              size="lg"
              aria-label="Sign in"
            >
              {loading ? 'Signing In...' : (
                <>
                  Sign In <i className="fas fa-arrow-right" aria-hidden="true"></i>
                </>
              )}
            </Button>

            {error && (
              <div className="text-center bg-red-50 dark:bg-red-500/10 border-2 border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-400 font-medium text-sm py-3 px-4 rounded-xl animate-shake" role="alert">
                <i className="fas fa-exclamation-circle mr-2" aria-hidden="true"></i>
                {error}
              </div>
            )}

            <div className="text-center mt-6">
              <button type="button" className="text-primary dark:text-accent hover:text-primary-dark dark:hover:text-primary font-medium text-base hover:underline transition-colors inline-flex items-center gap-2 bg-transparent border-none cursor-pointer">
                <i className="fas fa-key"></i>
                Forgot your password?
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
